import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    List,
    ListItem,
    ListItemText,
    Chip,
    CircularProgress,
    Alert,
    Grid
} from '@mui/material';
import { confirmGameSync } from '@api/cms';
import { toast } from 'react-toastify';

const GameSyncModal = ({ open, onClose, previewData, onSyncComplete, syncApiUrl }) => {
    const [syncing, setSyncing] = useState(false);

    if (!previewData) return null;

    const handleConfirmSync = async () => {
        setSyncing(true);
        try {
            const result = await confirmGameSync(syncApiUrl);
            if (result.success) {
                toast.success(`同步成功! 已同步 ${result.syncedGamesCount} 個遊戲`);
                if (result.errors && result.errors.length > 0) {
                    result.errors.forEach(error => toast.warning(error));
                }
                onSyncComplete();
                onClose();
            } else {
                toast.error(`同步失敗: ${result.message}`);
            }
        } catch (error) {
            toast.error(`同步失敗: ${error.message}`);
        } finally {
            setSyncing(false);
        }
    };

    const formatDateTime = (dateTime) => {
        if (!dateTime) return '從未同步';
        return new Date(dateTime).toLocaleString('zh-TW');
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Typography variant="h6">遊戲同步預覽</Typography>
            </DialogTitle>
            <DialogContent>
                <Box mb={3}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            <strong>警告:</strong> 此操作將完全替換現有的 AW 遊戲清單。請仔細檢查以下變更後再確認同步。
                        </Typography>
                    </Alert>
                    
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                                外部 API 遊戲數量: <strong>{previewData.totalExternalGames}</strong>
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                                目前本地遊戲數量: <strong>{previewData.currentLocalGames}</strong>
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                                上次同步時間: {formatDateTime(previewData.lastSyncedAt)}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                <Box>
                    {/* Games to Add */}
                    {previewData.gamesToAdd && previewData.gamesToAdd.length > 0 && (
                        <Box mb={2}>
                            <Typography variant="h6" color="success.main" gutterBottom>
                                新增遊戲 ({previewData.gamesToAdd.length})
                            </Typography>
                            <List dense sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                {previewData.gamesToAdd.slice(0, 10).map((game, index) => (
                                    <ListItem key={index} divider>
                                        <ListItemText
                                            primary={`${game.gameID} - ${game.nameCH}`}
                                            secondary={game.nameEN}
                                        />
                                        <Box>
                                            {game.enabled && <Chip label="啟用" color="success" size="small" sx={{ mr: 1 }} />}
                                            {game.isHot && <Chip label="熱門" color="error" size="small" />}
                                        </Box>
                                    </ListItem>
                                ))}
                                {previewData.gamesToAdd.length > 10 && (
                                    <ListItem>
                                        <ListItemText secondary={`... 還有 ${previewData.gamesToAdd.length - 10} 個遊戲`} />
                                    </ListItem>
                                )}
                            </List>
                        </Box>
                    )}

                    {/* Games to Update */}
                    {previewData.gamesToUpdate && previewData.gamesToUpdate.length > 0 && (
                        <Box mb={2}>
                            <Typography variant="h6" color="warning.main" gutterBottom>
                                更新遊戲 ({previewData.gamesToUpdate.length})
                            </Typography>
                            <List dense sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                {previewData.gamesToUpdate.slice(0, 10).map((game, index) => (
                                    <ListItem key={index} divider>
                                        <ListItemText
                                            primary={`${game.gameID} - ${game.nameCH}`}
                                            secondary={game.nameEN}
                                        />
                                        <Box>
                                            {game.enabled && <Chip label="啟用" color="success" size="small" sx={{ mr: 1 }} />}
                                            {game.isHot && <Chip label="熱門" color="error" size="small" />}
                                        </Box>
                                    </ListItem>
                                ))}
                                {previewData.gamesToUpdate.length > 10 && (
                                    <ListItem>
                                        <ListItemText secondary={`... 還有 ${previewData.gamesToUpdate.length - 10} 個遊戲`} />
                                    </ListItem>
                                )}
                            </List>
                        </Box>
                    )}

                    {/* Games to Remove */}
                    {previewData.gamesToRemove && previewData.gamesToRemove.length > 0 && (
                        <Box mb={2}>
                            <Typography variant="h6" color="error.main" gutterBottom>
                                移除遊戲 ({previewData.gamesToRemove.length})
                            </Typography>
                            <List dense sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                {previewData.gamesToRemove.slice(0, 10).map((game, index) => (
                                    <ListItem key={index} divider>
                                        <ListItemText
                                            primary={`${game.gameID} - ${game.nameCH}`}
                                            secondary={game.nameEN}
                                        />
                                    </ListItem>
                                ))}
                                {previewData.gamesToRemove.length > 10 && (
                                    <ListItem>
                                        <ListItemText secondary={`... 還有 ${previewData.gamesToRemove.length - 10} 個遊戲`} />
                                    </ListItem>
                                )}
                            </List>
                        </Box>
                    )}

                    {previewData.gamesToAdd.length === 0 && 
                     previewData.gamesToUpdate.length === 0 && 
                     previewData.gamesToRemove.length === 0 && (
                        <Alert severity="info">
                            沒有發現任何變更，遊戲清單已是最新狀態。
                        </Alert>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button 
                    onClick={onClose} 
                    disabled={syncing}
                    variant="outlined"
                    sx={{ color: 'text.primary' }}
                >
                    取消
                </Button>
                <Button 
                    onClick={handleConfirmSync} 
                    variant="contained" 
                    color="primary"
                    disabled={syncing || (
                        previewData.gamesToAdd.length === 0 && 
                        previewData.gamesToUpdate.length === 0 && 
                        previewData.gamesToRemove.length === 0
                    )}
                    startIcon={syncing ? <CircularProgress size={20} /> : null}
                >
                    {syncing ? '同步中...' : '確認同步'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default GameSyncModal;